import { memo, useCallback, useEffect } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSearchParams } from 'react-router-dom';

import type { StopType } from '@/types';
import type { PickingInfo } from '@deck.gl/core';

import getStationMarkerDeckLayers from '@/components/map/station-marker/station-marker-deck-layers';
import DeckGLOverlay from '@/components/map/train-marker/deckgl-overlay';
import useGetStationListData from '@/hooks/use-get-station-list-data';
import { useMapConfig } from '@/hooks/use-map-config';
import usePinnedItem from '@/hooks/use-pinned-item';
import useStationPinAnimation from '@/hooks/use-station-pin-animation';
import useStopPositionData from '@/hooks/use-stop-position-data';
import { useZoomThreshold } from '@/hooks/use-zoom-threshold';
import useChainStore from '@/store/useChainStore';
import {
  SEARCH_PARAM_DRAWER,
  SEARCH_PARAM_INCIDENT_ID,
  SEARCH_PARAM_INCIDENTS,
  SEARCH_PARAM_LOCK_PIN,
  SEARCH_PARAM_LOCK_PIN_TYPE,
  SEARCH_PARAM_SHOW,
  SEARCH_PARAM_STATION_ID,
  SEARCH_PARAM_STATIONS,
  SEARCH_PARAM_VEREINA_STATUS,
  SEARCH_PARAM_VIEW,
  SEARCH_PARAMS_ROUTE_ID,
} from '@/utils/constants/queries';
import { useDeckCursorHover } from '@/utils/helpers/train-simulation/use-deck-cursor-hover';

const StationMarker = () => {
  const { config } = useMapConfig();
  const { current: mapRef } = useMap();
  const [searchParams, setSearchParams] = useSearchParams();

  const isVisible = useZoomThreshold(mapRef, 9);
  const isLarge = useZoomThreshold(mapRef, 11);
  const stationPositionData = useStopPositionData();
  const { data } = useGetStationListData();
  const { pinnedItemId, pinnedItemType } = usePinnedItem();
  const { addToLast } = useChainStore();

  const selectedRouteId = searchParams.get(SEARCH_PARAMS_ROUTE_ID);
  const selectedStationId = searchParams.get(SEARCH_PARAM_STATION_ID);
  const isSidePanelOpen = !!searchParams.get(SEARCH_PARAM_SHOW);
  const isStationPinned = pinnedItemType === 'station';

  useStationPinAnimation(stationPositionData, pinnedItemId);

  const pinnedStationFeature = stationPositionData?.features?.find(
    ({ properties }) => String(properties?.id) === String(pinnedItemId)
  );

  const isPinnedStationAvailable = !!pinnedStationFeature;
  const showAllStations = config.nonCommercialStations;
  const isStationVisibleByConfig = isPinnedStationAvailable || showAllStations;

  const mapLockedStationId =
    pinnedItemId &&
    pinnedItemType === 'station' &&
    isPinnedStationAvailable &&
    isStationVisibleByConfig
      ? pinnedItemId
      : null;

  const { isLoading: isStationListLoading } = useGetStationListData();

  const shouldRemoveLockView =
    pinnedItemId && isStationPinned && (!isPinnedStationAvailable || !isStationVisibleByConfig);

  useEffect(() => {
    if (!!shouldRemoveLockView && !isStationListLoading) {
      setSearchParams(
        (prev) => {
          prev.delete(SEARCH_PARAM_LOCK_PIN);
          prev.delete(SEARCH_PARAM_LOCK_PIN_TYPE);
          prev.delete(SEARCH_PARAM_DRAWER);

          return prev;
        },
        { replace: true }
      );
    }
  }, [shouldRemoveLockView, isStationListLoading]);

  const handleClick = useCallback(
    (info: PickingInfo) => {
      if (!mapRef || !info.object) {
        return;
      }

      const clickedStation = info.object as StopType;

      if (clickedStation) {
        const stationId = clickedStation.id;
        const newSearchParams = new URLSearchParams(searchParams);
        const shouldAddToLast =
          (selectedRouteId || selectedStationId) && selectedStationId !== stationId;

        if (shouldAddToLast) {
          addToLast({
            [SEARCH_PARAMS_ROUTE_ID]: newSearchParams.get(SEARCH_PARAMS_ROUTE_ID),
            [SEARCH_PARAM_SHOW]: newSearchParams.get(SEARCH_PARAM_SHOW),
            [SEARCH_PARAM_STATION_ID]: newSearchParams.get(SEARCH_PARAM_STATION_ID),
          });
        }

        newSearchParams.delete(SEARCH_PARAMS_ROUTE_ID);
        newSearchParams.delete(SEARCH_PARAM_INCIDENT_ID);
        newSearchParams.delete(SEARCH_PARAM_INCIDENTS);
        newSearchParams.set(SEARCH_PARAM_SHOW, SEARCH_PARAM_STATIONS);
        newSearchParams.set(SEARCH_PARAM_STATION_ID, stationId);
        newSearchParams.delete(SEARCH_PARAM_VIEW);
        newSearchParams.delete(SEARCH_PARAM_VEREINA_STATUS);

        if (pinnedItemId && !isSidePanelOpen) {
          newSearchParams.set(SEARCH_PARAM_LOCK_PIN, pinnedItemId);
        }

        newSearchParams.delete(SEARCH_PARAM_DRAWER);

        if (pinnedItemType) {
          newSearchParams.set(SEARCH_PARAM_LOCK_PIN_TYPE, pinnedItemType);
        }

        setSearchParams(newSearchParams, { replace: true });
      }
    },
    [
      mapRef,
      pinnedItemId,
      pinnedItemType,
      isSidePanelOpen,
      selectedStationId,
      selectedRouteId,
      searchParams,
    ]
  );

  const deckLayers = getStationMarkerDeckLayers(
    data,
    handleClick,
    isVisible,
    isLarge,
    mapLockedStationId,
    isStationVisibleByConfig
  );

  const handleHover = useDeckCursorHover(mapRef);

  return <DeckGLOverlay layers={deckLayers} onHover={handleHover} />;
};

export default memo(StationMarker);
